import { Alert, Button, Modal, Spin, message } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdArrowBack } from 'react-icons/md'
import { TbFolderCog } from 'react-icons/tb'
import { Link, useNavigate, useParams } from 'react-router-dom'

import {
    DiskNodeIcon,
    diskErrorText,
    diskFolderUrl,
    formatBytes,
    openDiskFile,
    pluralUk,
} from '@/Containers/DiskSection/DiskContainer/Hooks'
import { withDiskFeature } from '@/Containers/DiskSection/DiskContainer/Hooks'
import { Panel, formatInt, shownOf, useChartColors } from '@/Ship/Components/Dashboard/Overview/shared'
import { classNames } from '@/Ship/Helpers/classNames'

import { CategoryStack, QuotaBar, StateDonut, filesLabel } from '../Components/DiskAdminCharts'
import { DiskAdminShell } from '../Components/DiskAdminShell'
import { QuotaModal } from '../Components/QuotaModal'
import { CleanupRequestModal } from '../Components/CleanupRequestModal'
import { PersonAvatar, SpaceIcon, StorageBadges, storageSubtitle } from '../Components/StorageName'
import { UsageTrend } from '../Components/UsageTrend'
import { activityLabel, formatDate, formatDateTime } from '../Helpers/diskAdminFormat'
import {
    diskAdminAuditUrl,
    diskAdminOffboardingUrl,
    diskAdminStorageUrl,
    diskAdminStoragesUrl,
    parseDiskAdminStorageId,
} from '../Helpers/diskAdminRoutes'
import { diskAdminStorageStore as store } from '../Stores/DiskAdminStorageStore'
import { diskAdminStoragesStore } from '../Stores/DiskAdminStoragesStore'
import { diskPolicyStore } from '../Stores/DiskPolicyStore'
import { LoadDiskAdminPersonTask } from '../Tasks/LoadDiskAdminPersonTask'
import { RestoreDiskStorageToOwnerTask } from '../Tasks/RestoreDiskStorageToOwnerTask'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'
import type { DiskFileKind } from '@/Containers/DiskSection/DiskContainer/Hooks'
import type { DiskAdminPerson, DiskAdminStorageCard } from '../Contracts/DiskAdmin'
import type { ReactNode } from 'react'

import overview from '@/Ship/Components/Dashboard/Overview/Overview.module.css'
import styles from '../Components/DiskAdmin.module.css'

const Stat = ({ label, children }: { label: string; children: ReactNode }) => (
    <div className={classNames(styles['heroStat'])}>
        <span className={classNames(styles['heroStatLabel'])}>{label}</span>
        <span className={classNames(styles['heroStatValue'])}>{children}</span>
    </div>
)

const BackLink = () => (
    <Link to={diskAdminStoragesUrl()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <MdArrowBack /> Диски
    </Link>
)

/** Скільки днів до строку рішення (0 — сьогодні, від'ємне — прострочено). */
const daysLeft = (dueAt: string | null): number | null =>
    dueAt ? dayjs(dueAt).startOf('day').diff(dayjs().startOf('day'), 'day') : null

/** Шапка: хто/що, позначки, обсяги, квота й дії (як у Бітріксі). */
const Hero = observer(({ card, actions }: { card: DiskAdminStorageCard; actions: ReactNode }) => {
    const { storage, totals } = card
    const personal = storage.type === 'personal'
    const title = personal ? `Диск ${storage.owner?.name ?? storage.title}` : storage.title

    return (
        <section className={classNames(styles['hero'])} data-block="disk-admin-hero">
            <div className={classNames(styles['heroMain'])} style={{ flex: '1 1 420px' }}>
                {personal && storage.owner ? (
                    <PersonAvatar person={storage.owner} size={56} />
                ) : (
                    <SpaceIcon type={storage.type} state={storage.state} size={56} />
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 className={classNames(styles['heroTitle'])} data-testid="disk-admin-storage-title">
                        {title}
                    </h2>
                    <div className={classNames(styles['heroMeta'])}>
                        <span>{storageSubtitle(storage.type, storage.owner)}</span>
                        <StorageBadges
                            state={storage.state}
                            departed={storage.owner !== null && storage.owner.status !== undefined && storage.owner.status !== 'active'}
                            hasDisk
                        />
                    </div>
                    <div className={classNames(styles['heroStats'])}>
                        <div style={{ minWidth: 200 }}>
                            <span className={classNames(styles['heroStatLabel'])}>
                                Зайнято{storage.quotaIsDefault && storage.quota !== null ? ' · квота за замовчуванням' : ''}
                            </span>
                            <QuotaBar used={storage.used + storage.reserved} quota={storage.quota} percent={storage.percent} />
                        </div>
                        <Stat label="Файлів">{formatInt(totals.files)}</Stat>
                        <Stat label="Папок">{formatInt(totals.folders)}</Stat>
                        <Stat label="Остання активність">
                            <span title={formatDateTime(card.lastActivityAt)}>{activityLabel(card.lastActivityAt)}</span>
                        </Stat>
                        <Stat label="Останнє завантаження">{formatDate(card.lastUploadAt)}</Stat>
                    </div>
                </div>
            </div>
            <div className={classNames(styles['heroActions'])}>{actions}</div>
        </section>
    )
})

/** Що можна звільнити й скільки місця простір реально займає на сервері. */
const ReclaimAndPhysical = ({ card }: { card: DiskAdminStorageCard }) => (
    <div className={classNames(styles['grid2'])}>
        <Panel block="disk-admin-storage-reclaim" title="Що можна звільнити" subtitle="Квоту звільняє остаточне видалення">
            <div className={classNames(styles['reclaim'])}>
                <div className={classNames(styles['reclaimRow'])}>
                    <span className={classNames(styles['reclaimTitle'])}>Старі версії</span>
                    <span className={classNames(styles['reclaimValue'])}>{formatBytes(card.reclaimable.oldVersions)}</span>
                    <span className={classNames(styles['reclaimNote'])}>
                        {formatInt(card.reclaimable.oldVersionsCount)}{' '}
                        {pluralUk(card.reclaimable.oldVersionsCount, 'версія', 'версії', 'версій')}
                    </span>
                </div>
                <div className={classNames(styles['reclaimRow'])}>
                    <span className={classNames(styles['reclaimTitle'])}>Кошик</span>
                    <span className={classNames(styles['reclaimValue'])}>{formatBytes(card.reclaimable.trash)}</span>
                    <span className={classNames(styles['reclaimNote'])}>
                        {filesLabel(card.totals.trashFiles)} · старше {card.reclaimable.trashOlderThanDays} дн. —{' '}
                        {formatBytes(card.reclaimable.trashOlderThan)}
                    </span>
                </div>
                <div className={classNames(styles['reclaimRow'])}>
                    <span className={classNames(styles['reclaimTitle'])}>Дублікати в межах простору</span>
                    <span className={classNames(styles['reclaimValue'])}>
                        {formatBytes(card.reclaimable.duplicates.bytes)}
                    </span>
                    <span className={classNames(styles['reclaimNote'])}>
                        {card.reclaimable.duplicates.groups > 0
                            ? `${filesLabel(card.reclaimable.duplicates.files)} однакового вмісту`
                            : 'Однакових файлів немає'}
                    </span>
                </div>
            </div>
        </Panel>
        <Panel block="disk-admin-storage-physical" title="На сервері" subtitle="Однаковий вміст зберігається один раз">
            <div className={classNames(styles['reclaim'])}>
                <div className={classNames(styles['reclaimRow'])}>
                    <span className={classNames(styles['reclaimTitle'])}>Логічно — те, що рахує квота</span>
                    <span className={classNames(styles['reclaimValue'])}>{formatBytes(card.physical.logical)}</span>
                </div>
                <div className={classNames(styles['reclaimRow'])}>
                    <span className={classNames(styles['reclaimTitle'])}>Фізично — різні файли простору</span>
                    <span className={classNames(styles['reclaimValue'])}>{formatBytes(card.physical.physical)}</span>
                    <span className={classNames(styles['reclaimNote'])}>
                        заощаджено дедуплікацією {formatBytes(card.physical.dedupSaved)} · лише в цьому просторі{' '}
                        {formatBytes(card.physical.exclusive)}
                    </span>
                </div>
                <div className={classNames(styles['reclaimRow'])}>
                    <span className={classNames(styles['reclaimTitle'])}>Чекає прибирання на сервері (уся компанія)</span>
                    <span className={classNames(styles['reclaimValue'])}>{formatBytes(card.pendingGc.bytes)}</span>
                    <span className={classNames(styles['reclaimNote'])}>
                        {filesLabel(card.pendingGc.blobs)} без посилань · прибираються до {card.pendingGc.days} дн.
                    </span>
                </div>
            </div>
        </Panel>
    </div>
)

/** Склад простору — лише коли бек його віддав (`contentVisible`): спільний простір, диск звільненого. */
const ContentPanels = ({ card }: { card: DiskAdminStorageCard }) => {
    const chart = useChartColors()
    const folders = card.topFolders ?? []
    const root = card.rootFiles
    const max = Math.max(1, ...folders.map((folder) => folder.bytes))
    // Спільний простір адміністратор відкриває й на звичайному екрані диска (він там «Менеджер»)
    const browsable = card.storage.type !== 'personal'

    return (
        <div className={classNames(styles['grid2'])}>
            <Panel
                block="disk-admin-folders"
                title="Папки за розміром"
                subtitle="Верхній рівень, з усіма версіями файлів усередині"
                extra={shownOf(folders.length, card.topFoldersTotal ?? folders.length, 'папок')}
                empty={folders.length === 0 ? 'Папок верхнього рівня немає' : false}
            >
                <div className={classNames(overview['bars'])}>
                    {folders.map((folder) => {
                        const row = (
                            <div className={classNames(overview['barRow'])}>
                                <span className={classNames(overview['barLabel'])} title={folder.name}>
                                    {folder.name}
                                    <span className={classNames(overview['barMuted'])}>{filesLabel(folder.files)}</span>
                                </span>
                                <span className={classNames(overview['barValue'])}>{formatBytes(folder.bytes)}</span>
                                <div className={classNames(overview['track'])}>
                                    <div
                                        className={classNames(overview['fill'])}
                                        style={{ width: `${(folder.bytes / max) * 100}%`, background: chart.second }}
                                    />
                                </div>
                            </div>
                        )

                        return browsable ? (
                            <Link
                                key={folder.uuid}
                                to={diskFolderUrl(card.storage.uuid, folder.uuid)}
                                className={classNames(styles['rowLink'])}
                                data-folder={folder.uuid}
                            >
                                {row}
                            </Link>
                        ) : (
                            <div key={folder.uuid} data-folder={folder.uuid}>
                                {row}
                            </div>
                        )
                    })}
                </div>
            </Panel>
            <Panel
                block="disk-admin-root-files"
                title="Файли в корені"
                subtitle="Найбільші; відкриваються в переглядачі диска, відкриття — у журнал"
                extra={root ? `${filesLabel(root.files)} · ${formatBytes(root.bytes)}` : null}
                empty={!root || root.items.length === 0 ? 'У корені немає файлів' : false}
            >
                <div data-testid="disk-admin-root-files">
                    {(root?.items ?? []).map((file) => (
                        <button
                            key={file.uuid}
                            type="button"
                            className={classNames(styles['fileRow'])}
                            onClick={() => openDiskFile(file.uuid, { admin: true })}
                            data-file={file.uuid}
                        >
                            <DiskNodeIcon kind={file.kind as DiskFileKind} size={28} />
                            <span className={classNames(overview['barLabel'])} title={file.name}>
                                {file.name}
                            </span>
                            <span className={classNames(overview['barValue'])}>{formatBytes(file.size)}</span>
                        </button>
                    ))}
                </div>
                {browsable ? (
                    <div className={classNames(overview['footnote'])}>
                        <Link to={diskFolderUrl(card.storage.uuid)}>Відкрити простір на диску</Link>
                    </div>
                ) : null}
            </Panel>
        </div>
    )
}

/** Диск звільненого: три групи майстра (лише підсумки — рядки в справі). */
const SharedGroups = ({ shared }: { shared: NonNullable<DiskAdminStorageCard['shared']> }) => {
    const groups = [
        { key: 'active', title: 'Спільні й використовуються', data: shared.active },
        { key: 'idle', title: 'Спільні, не використовуються', data: shared.idle },
        { key: 'own', title: 'Лише його', data: shared.own },
    ] as const

    return (
        <Panel block="disk-admin-shared" title="Спільне з колегами" subtitle="Як файли розійдуться в майстрі рішення">
            <div className={classNames(styles['reclaim'])}>
                {groups.map((group) => (
                    <div key={group.key} className={classNames(styles['reclaimRow'])} data-group={group.key}>
                        <span className={classNames(styles['reclaimTitle'])}>{group.title}</span>
                        <span className={classNames(styles['reclaimValue'])}>{formatBytes(group.data.bytes)}</span>
                        <span className={classNames(styles['reclaimNote'])}>
                            {filesLabel(group.data.files)} · {formatInt(group.data.rows)}{' '}
                            {pluralUk(group.data.rows, 'елемент', 'елементи', 'елементів')} верхнього рівня
                        </span>
                    </div>
                ))}
            </div>
        </Panel>
    )
}

/** Співробітник без «Мого диска» (`/disk/admin/storage/user:<uuid>`): квотою диск можна створити наперед. */
const NoDiskCard = observer(({ userUuid }: { userUuid: string }) => {
    const navigate = useNavigate()
    const cached = diskAdminStoragesStore.rows.find((row) => row.key === `user:${userUuid}`)?.owner ?? null
    const [person, setPerson] = useState<DiskAdminPerson | null>(cached)
    const [quotaOpen, setQuotaOpen] = useState(false)

    useEffect(() => {
        if (person === null) {
            LoadDiskAdminPersonTask(userUuid)
                .then(setPerson)
                .catch(() => setPerson({ uuid: userUuid, name: null }))
        }
        if (diskPolicyStore.policy === null && !diskPolicyStore.isLoading) void diskPolicyStore.load()
    }, [userUuid, person])

    const personalQuota = diskPolicyStore.policy?.policy.personalQuotaBytes ?? null

    return (
        <>
            <BackLink />
            <section className={classNames(styles['hero'])} data-block="disk-admin-hero">
                <div className={classNames(styles['heroMain'])}>
                    {person ? <PersonAvatar person={person} size={56} /> : null}
                    <div>
                        <h2 className={classNames(styles['heroTitle'])}>{person?.name ? `Диск ${person.name}` : 'Диск співробітника'}</h2>
                        <div className={classNames(styles['heroMeta'])}>
                            <span>{person?.position ?? 'Мій диск'}</span>
                            <StorageBadges state="active" departed={person?.status !== undefined && person.status !== 'active'} hasDisk={false} />
                        </div>
                    </div>
                </div>
                <div className={classNames(styles['heroActions'])}>
                    <Button onClick={() => setQuotaOpen(true)}>Задати квоту</Button>
                </div>
            </section>
            <Alert
                type="info"
                showIcon
                title="«Мого диска» в співробітника ще немає"
                description="Він створюється, коли співробітник уперше відкриває «Диск», або коли адміністратор задає йому квоту."
            />
            <QuotaModal
                open={quotaOpen}
                subject={person?.name ?? 'Співробітник'}
                targets={{ storages: [], users: [userUuid] }}
                used={0}
                currentQuota={null}
                defaultLabel={`За замовчуванням (${personalQuota !== null ? formatBytes(personalQuota) : 'квота політики'})`}
                defaultQuota={personalQuota}
                onClose={() => setQuotaOpen(false)}
                onSaved={(result) => {
                    setQuotaOpen(false)
                    void message.success('Диск створено, квоту задано')
                    const created = result.items[0]?.uuid
                    if (created) navigate(diskAdminStorageUrl(created), { replace: true })
                }}
            />
        </>
    )
})

/**
 * Картка диска чи простору `/disk/admin/storage/<uuid>` (`DISK-006` §3.3,
 * наряд Ф4): шапка з квотою, «Чим зайнято», стан місця, міні-тренд, що можна
 * звільнити, фізичне й логічне.
 *
 * **Приватність (ADR 052, О-3).** Активний «Мій диск» приходить без складу
 * (`contentVisible: false`) — назв файлів у DOM немає, бо їх немає у
 * відповіді. Замість складу — пояснення й «Попросити звільнити місце».
 * Спільний простір і диск звільненого — папки за розміром і файли кореня;
 * файл відкривається тим самим переглядачем диска з адмінським посиланням
 * (кожне відкриття — у журнал адміністратора).
 */
const StoragePage = observer(() => {
    const params = useParams()
    const target = parseDiskAdminStorageId(params['id'])
    const storageUuid = target?.kind === 'storage' ? target.uuid : null
    const [quotaOpen, setQuotaOpen] = useState(false)
    const [cleanupOpen, setCleanupOpen] = useState(false)

    useEffect(() => {
        if (storageUuid !== null) store.open(storageUuid)
        if (diskPolicyStore.policy === null && !diskPolicyStore.isLoading) void diskPolicyStore.load()
    }, [storageUuid])

    if (target?.kind === 'user') {
        return (
            <DiskAdminShell tab="storages" documentTitle="Диск співробітника — адміністрування диска">
                <NoDiskCard userUuid={target.uuid} />
            </DiskAdminShell>
        )
    }

    const card = store.card !== null && store.card.storage.uuid === storageUuid ? store.card : null

    if (card === null) {
        return (
            <DiskAdminShell tab="storages" documentTitle="Диск — адміністрування диска">
                <BackLink />
                {store.error !== null ? (
                    <Alert type="error" showIcon title={store.error} />
                ) : target === null ? (
                    <Alert type="error" showIcon title="Диск не вказано" />
                ) : (
                    <div style={{ padding: 48, textAlign: 'center' }}>
                        <Spin />
                    </div>
                )}
            </DiskAdminShell>
        )
    }

    const { storage } = card
    const activePersonal = storage.type === 'personal' && storage.state === 'active'
    const ownerName = storage.owner?.name ?? storage.title
    const left = daysLeft(card.offboarding?.dueAt ?? null)

    const restore = () =>
        Modal.confirm({
            title: 'Повернути диск власнику?',
            content: `Архів знову стане «Моїм диском» ${ownerName}. Доступ інших учасників до архіву буде прибрано.`,
            okText: 'Повернути',
            cancelText: 'Скасувати',
            onOk: async () => {
                try {
                    await RestoreDiskStorageToOwnerTask(storage.uuid)
                    void message.success('Диск повернуто власнику')
                    void store.reload()
                } catch (error) {
                    void message.error(diskErrorText(error, 'Не вдалося повернути диск'))
                }
            },
        })

    const actions = (
        <>
            {card.permissions.quota ? (
                <Button type="primary" onClick={() => setQuotaOpen(true)} data-testid="disk-admin-change-quota">
                    Змінити квоту
                </Button>
            ) : null}
            {activePersonal ? (
                <Button onClick={() => setCleanupOpen(true)} data-testid="disk-admin-cleanup">
                    Попросити звільнити місце
                </Button>
            ) : null}
            {storage.state === 'frozen' && card.offboarding?.uuid ? (
                <Link to={diskAdminOffboardingUrl(card.offboarding.uuid)}>
                    <Button type="primary" ghost>
                        Вирішити долю диска
                    </Button>
                </Link>
            ) : null}
            {storage.state === 'archived' && card.permissions.content ? (
                <Button onClick={restore}>Повернути власнику</Button>
            ) : null}
            <Link to={diskAdminAuditUrl(storage.uuid)}>
                <Button>Журнал диска</Button>
            </Link>
        </>
    )

    const personalQuota = diskPolicyStore.policy?.policy.personalQuotaBytes ?? null

    return (
        <DiskAdminShell tab="storages" documentTitle={`${storage.type === 'personal' ? `Диск ${ownerName}` : storage.title} — адміністрування диска`}>
            <BackLink />
            <Hero card={card} actions={actions} />

            {storage.state === 'frozen' ? (
                <Alert
                    type="warning"
                    showIcon
                    data-testid="disk-admin-frozen"
                    title={`Деактивовано ${formatDate(storage.frozenAt)}. ${
                        card.offboarding?.dueAt
                            ? `Рішення до ${formatDate(card.offboarding.dueAt)} — ${
                                  left !== null && left < 0
                                      ? `прострочено на ${-left} дн.`
                                      : `лишилось ${left ?? 0} дн.`
                              }`
                            : 'Диск чекає рішення'
                    }. Кошик не очищується до рішення.`}
                />
            ) : null}

            {!card.contentVisible ? (
                <Alert
                    type="info"
                    showIcon
                    data-testid="disk-admin-private"
                    title="Склад особистого диска бачить лише власник"
                    description="Назви файлів активного співробітника не показуються. Можна змінити квоту або попросити його звільнити місце."
                />
            ) : null}

            <Panel
                block="disk-admin-storage-categories"
                title="Чим зайнято"
                subtitle="Поточні версії файлів поза кошиком, за типами"
                extra={formatBytes(card.byCategory.reduce((sum, row) => sum + row.bytes, 0))}
            >
                <CategoryStack rows={card.byCategory} />
            </Panel>

            <div className={classNames(styles['grid2'])}>
                <Panel block="disk-admin-storage-state" title="Стан місця" subtitle="З чого складається зайняте">
                    <StateDonut byState={card.byState} />
                </Panel>
                <Panel
                    block="disk-admin-storage-trend"
                    title="Тренд, 30 днів"
                    subtitle="Нічні знімки простору й поточний стан"
                    loading={store.trend === null}
                    skeletonRows={5}
                >
                    {store.trend ? <UsageTrend trend={store.trend} height={180} /> : null}
                </Panel>
            </div>

            {card.shared ? <SharedGroups shared={card.shared} /> : null}
            {card.contentVisible ? <ContentPanels card={card} /> : null}
            <ReclaimAndPhysical card={card} />

            <QuotaModal
                open={quotaOpen}
                subject={storage.type === 'personal' ? `Диск ${ownerName}` : storage.title}
                targets={{ storages: [storage.uuid], users: [] }}
                used={storage.used + storage.reserved}
                currentQuota={storage.quotaBytes}
                defaultLabel={
                    storage.type === 'personal'
                        ? `За замовчуванням (${formatBytes(storage.defaultQuota ?? personalQuota ?? 0)})`
                        : 'За замовчуванням (без власної квоти)'
                }
                defaultQuota={storage.type === 'personal' ? (storage.defaultQuota ?? personalQuota) : null}
                onClose={() => setQuotaOpen(false)}
                onSaved={(result) => {
                    setQuotaOpen(false)
                    void message.success('Квоту змінено')
                    const warning = result.warnings[0]
                    if (warning) void message.warning(`${warning.message} Бракує ${formatBytes(warning.shortBytes)}.`)
                    void store.reload()
                }}
            />
            {activePersonal ? (
                <CleanupRequestModal
                    open={cleanupOpen}
                    storageUuid={storage.uuid}
                    ownerName={ownerName}
                    onClose={() => setCleanupOpen(false)}
                    onSent={() => {
                        setCleanupOpen(false)
                        void message.success('Прохання надіслано')
                    }}
                />
            ) : null}
        </DiskAdminShell>
    )
})

export const pageConfig: IPageConfig = {
    key: 'Disk.AdminStorage',
    route: '/disk/admin/storage/:id',
    title: 'Диск — адміністрування диска',
    icon: <TbFolderCog />,
}

export default withDiskFeature(StoragePage)
